// The Last Supper and the endings (docs/04 §23, docs/06 §7), Endless INSATIABLE (§22), and the
// good path's payphone (docs/06 §4). Paying the Last Supper opens a choice of physical actions;
// after any ending the machine asks REMEMBER? — keep playing into Endless, or forget your answers.
import { B } from '../content/balance';
import { phoneKeys, type PhoneScript } from '../content/phone';
import { LEDGER_PAGES } from '../content/registry';
import { say, sayScript } from './dialogue';
import { openIntermission } from './intermission';
import type { Modifiers } from './modifiers';
import type { Decimal } from './decimal';
import type { EndingId, GameEvent, GameState, IntermissionState, MetaState, RunSummary } from './types';

export function goodPathReady(meta: MetaState): boolean {
  return meta.lifetime.dealsRefused >= B.PHONE_DEALS && B.PLUG_PARTS.every((p) => meta.lifetime.plugParts.includes(p));
}

export function endingChoices(meta: MetaState): EndingId[] {
  const out: EndingId[] = ['sated'];
  if (meta.flags.covenant) out.push('maw');
  if (goodPathReady(meta)) out.push('unplugged');
  return out;
}

/** Paid tribute: the Last Supper leads to the choice, everything else to the intermission. */
export function afterTribute(
  state: GameState,
  mods: Modifiers,
  early: number,
  bonus: Decimal,
  souls: number,
  events: GameEvent[],
  beaten?: IntermissionState['reckoning'],
): void {
  const run = state.run!;
  if (beaten?.id === 'lastsupper') {
    run.phase = 'ending';
    run.spin = null;
    sayScript(state, 'lastSupperPaid', events, 'adoring');
    events.push({ e: 'ENDING_CHOICE', available: endingChoices(state.meta) });
    return;
  }
  openIntermission(state, mods, early, bonus, souls, events, beaten);
}

export function chooseEnding(state: GameState, ending: EndingId, events: GameEvent[]): string | undefined {
  const run = state.run;
  if (!run || run.phase !== 'ending') return 'no choice to make';
  if (!endingChoices(state.meta).includes(ending)) return 'not unlocked';
  const meta = state.meta;
  const first = !meta.lifetime.endings.includes(ending);
  if (first) meta.lifetime.endings.push(ending);
  meta.flags.endlessUnlocked = true;
  meta.flags[`cosmetic_${ending}`] = true;
  // The Covenant binds you only until an ending.
  delete meta.flags.covenant;
  meta.souls += run.soulsPending;
  meta.lifetime.soulsEarned += run.soulsPending;
  const summary: RunSummary = {
    n: run.window.n,
    paid: run.stats.tributesPaid,
    souls: run.soulsPending,
    bestWin: run.stats.bestWin,
    spins: run.stats.spins,
    reason: 'ending',
    size: run.size,
    reckonings: run.reck.beaten,
    ending,
  };
  meta.lastRun = summary;
  meta.history = [summary, ...(meta.history ?? [])].slice(0, B.HISTORY_LENGTH);
  run.soulsPending = 0;
  run.phase = 'remember';
  events.push({ e: 'ENDING', ending, first });
  sayScript(state, ending === 'sated' ? 'sated' : ending === 'maw' ? 'becomeMaw' : 'unplugged', events, ending === 'unplugged' ? 'dead' : 'pleased');
  return undefined;
}

export function remember(state: GameState, mods: Modifiers, choice: 'endless' | 'forget', events: GameEvent[]): string | undefined {
  const run = state.run;
  if (!run || run.phase !== 'remember') return 'nothing to remember';
  events.push({ e: 'REMEMBERED', choice });
  if (choice === 'endless') {
    // INSATIABLE: the run goes on past Tribute 40, faster and hungrier.
    run.flags.endless = 1;
    say(state, 'endless', events, undefined, { force: true });
    openIntermission(state, mods, 0, run.coins.mul(0), 0, events);
    return undefined;
  }
  // Forget your answers: the path counters reset; everything else stays.
  const l = state.meta.lifetime;
  l.dealsAccepted = 0;
  l.dealsRefused = 0;
  l.plugParts = [];
  delete state.meta.flags.phoneFirst;
  run.phase = 'consumed';
  run.endReason = 'offer';
  return undefined;
}

/** The payphone rings when Dolores has something to say (at most once per intermission). */
export function phoneScriptFor(meta: MetaState): PhoneScript | null {
  if (meta.lifetime.dealsRefused < B.PHONE_DEALS) return null;
  if (!meta.flags.phoneFirst) return 'first';
  const missing = B.PLUG_PARTS.find((p) => !meta.lifetime.plugParts.includes(p)) as PhoneScript | undefined;
  if (missing && !meta.flags[`phone_${missing}`]) return missing;
  if (!missing && !meta.flags.phone_allFound) return 'allFound';
  return null;
}

export function ringPhone(state: GameState, im: IntermissionState, events: GameEvent[]): void {
  const script = phoneScriptFor(state.meta);
  if (!script) return;
  im.phone = { script, answered: false };
  events.push({ e: 'PHONE_RING' });
}

export function answerPhone(state: GameState, events: GameEvent[]): string | undefined {
  const im = state.run?.intermission;
  if (!im?.phone || im.phone.answered) return 'no call';
  im.phone.answered = true;
  const script = im.phone.script as PhoneScript;
  state.meta.flags[script === 'first' ? 'phoneFirst' : `phone_${script}`] = true;
  events.push({ e: 'PHONE_CALL', script, lines: phoneKeys(script).length });
  return undefined;
}

/** The missing-person flyer (docs/06 §8): in Act III its phone number is Dolores's — and page 1. */
export function readFlyer(state: GameState, mods: Modifiers, events: GameEvent[]): string | undefined {
  if (!mods.features.has('eyes')) return 'nothing to read';
  const first = LEDGER_PAGES[0]!.id;
  if (state.meta.lifetime.ledgerPages.includes(first)) return 'already found';
  state.meta.lifetime.ledgerPages.push(first);
  events.push({ e: 'PAGE_FOUND', id: first });
  return undefined;
}
